import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { createGoalV2 } from 'Shared/actions/Goals.actions';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import SubGoalField from './SubGoalField/SubGoalField';
import GoalDetailsPageAiSuggestedSubGoalsOrActionItems from '../GoalActionItems/GoalDetailsPageAiSuggestedSubGoalsOrActionItems/GoalDetailsPageAiSuggestedSubGoalsOrActionItems';
import CreateGoalModal from 'Dashboard/components/CreateOrUpdateGoalPageV3/CreateGoalModal';

// Selectors
import { getGoalById } from 'Shared/selectors/Goals.selectors';

// Utils
import { generateGoalV2 } from 'Dashboard/components/CreateOrUpdateGoalPageV3/utils';

type Props = {
  goalId: number;
};

function AddSubGoal({ goalId }: Props) {
  const dispatch = useDispatch();
  const [isActive, toggleIsActive] = useToggle(false);
  const [isNewModalOpen, toggleIsNewModalOpen] = useToggle(false);

  const handleActiveToggle = () => toggleIsActive();

  const createNewSubGoal = useCallback(
    values => {
      return dispatch(createGoalV2({ goal: generateGoalV2(values) }));
    },
    [dispatch]
  );

  const goal = useSelector(state => getGoalById(state, { id: goalId })?.toJS());

  // Create a bunch of sub goals by executing create sub goal one after the other
  const bulkCreateSubgoals = bodies => {
    return bodies.reduce((promiseChain, body) => {
      const subgoalValues = {
        name: body.split(' - ')[0],
        description: body.split(' - ')[1],
        parentId: goalId,
        workspaceId: goal.workspaceSlug,
        icon: goal.icon,
        endDate: goal.endDate,
        assignerId: goal.assignerId,
        assigneeId: goal.assigneeId,
      };
      return promiseChain.then(() => createNewSubGoal(subgoalValues));
    }, Promise.resolve());
  };

  if (!goal) return null;

  return (
    <>
      <div className="AddSubGoal">
        {isActive ? (
          <SubGoalField goalId={goalId} onActiveToggle={handleActiveToggle} />
        ) : (
          <WpButtonGroup spacing="md">
            <WpButton
              qaTest="add-sub-goal-button"
              onClick={handleActiveToggle}
              variant="tertiary"
              size="xs"
              square
              slimPadding
            >
              <WpIcon name="link" size="sm" />
              Link Sub-Goal
            </WpButton>
            <WpButton variant="tertiary" onClick={toggleIsNewModalOpen} size="xs" square slimPadding>
              <WpIcon name="plus" size="sm" />
              New Sub-Goal
            </WpButton>
            <GoalDetailsPageAiSuggestedSubGoalsOrActionItems
              goalSlug={goal.slug}
              handleBulkAddItems={bulkCreateSubgoals}
              suggestedObjectType={'subgoal'}
            />
          </WpButtonGroup>
        )}
      </div>
      <CreateGoalModal isOpen={isNewModalOpen} toggleIsOpen={toggleIsNewModalOpen} parentId={goalId} />
    </>
  );
}

export default AddSubGoal;
